/**
 * Sleep Reminder Service
 *
 * Handles scheduling local notifications for baby sleep reminders.
 * Currently uses Web Notifications. Native notifications can be added
 * by installing @capacitor/local-notifications.
 */

import { isNative } from './capacitor';

// Store active reminder timeouts
const activeReminders = new Map<string, number>();

/**
 * Schedule a sleep reminder notification
 * @param entryId - Unique ID for the sleep entry
 * @param minutesFromNow - Minutes from now to send reminder (30, 60, 90, etc.)
 * @returns Promise<boolean> - Whether scheduling was successful
 */
export async function scheduleSleepReminder(
  entryId: string,
  minutesFromNow: number
): Promise<boolean> {
  // Cancel any existing reminder for this entry
  cancelSleepReminder(entryId);

  // For native apps, try to use Capacitor Local Notifications if available
  if (isNative) {
    try {
      // Use globalThis to access the plugin if it was registered
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (globalThis as any).Capacitor;
      if (Capacitor?.Plugins?.LocalNotifications) {
        const LocalNotifications = Capacitor.Plugins.LocalNotifications;

        // Request permission first
        const permResult = await LocalNotifications.requestPermissions();
        if (permResult.display !== 'granted') {
          console.warn('Notification permission not granted');
          return scheduleWebNotification(entryId, minutesFromNow);
        }

        // Schedule the notification
        await LocalNotifications.schedule({
          notifications: [
            {
              id: parseInt(entryId.replace(/\D/g, '').slice(0, 8)) || Date.now(),
              title: 'Schlaf-Erinnerung',
              body: `Dein Baby schläft jetzt seit ${minutesFromNow} Minuten`,
              schedule: { at: new Date(Date.now() + minutesFromNow * 60 * 1000) },
              sound: 'default',
              smallIcon: 'ic_notification',
              iconColor: '#0B9488',
            },
          ],
        });

        return true;
      }
    } catch (error) {
      console.log('Local Notifications not available:', error);
    }
  }

  // Fallback to web notifications
  return scheduleWebNotification(entryId, minutesFromNow);
}

/**
 * Schedule a web notification as fallback
 */
async function scheduleWebNotification(
  entryId: string,
  minutesFromNow: number
): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported in this browser');
    return false;
  }

  // Request permission if needed
  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return false;
    }
  }

  if (Notification.permission !== 'granted') {
    return false;
  }

  // Schedule notification via setTimeout
  const timeoutId = window.setTimeout(() => {
    new Notification('Schlaf-Erinnerung', {
      body: `Dein Baby schläft jetzt seit ${minutesFromNow} Minuten`,
      icon: 'https://parentpath.de/web-app-manifest-192x192.png',
      tag: `sleep-reminder-${entryId}`,
    });
    activeReminders.delete(entryId);
  }, minutesFromNow * 60 * 1000);

  activeReminders.set(entryId, timeoutId);
  return true;
}

/**
 * Cancel a scheduled sleep reminder
 */
export async function cancelSleepReminder(entryId: string): Promise<void> {
  // Cancel web timeout if exists
  const timeoutId = activeReminders.get(entryId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    activeReminders.delete(entryId);
  }

  // Cancel native notification if available
  if (isNative) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (globalThis as any).Capacitor;
      if (Capacitor?.Plugins?.LocalNotifications) {
        const notificationId = parseInt(entryId.replace(/\D/g, '').slice(0, 8)) || 0;
        await Capacitor.Plugins.LocalNotifications.cancel({
          notifications: [{ id: notificationId }],
        });
      }
    } catch {
      // Local Notifications not available
    }
  }
}

/**
 * Available reminder intervals in minutes
 */
export const SLEEP_REMINDER_OPTIONS = [
  { value: 0, label: 'Keine' },
  { value: 30, label: '30 Min' },
  { value: 60, label: '1 Std' },
  { value: 90, label: '1,5 Std' },
  { value: 120, label: '2 Std' },
] as const;
